/**
 * AssetInspectionsTab — monthly asset inspection schedule.
 * Shows overdue / this-month / never-inspected buckets, lets the user log
 * an inspection, and generates a printable monthly PDF report.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CalendarCheck, AlertCircle, FileDown, ClipboardCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const CONDS = ["new", "good", "fair", "poor", "damaged"];

export default function AssetInspectionsTab({ wsId }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [inspecting, setInspecting] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/workspaces/${wsId}/inspection-schedule?month=${month}`);
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (wsId) load(); }, [wsId, month]);   // eslint-disable-line

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/workspaces/${wsId}/inspection-schedule/pdf?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `inspection-schedule-${month}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "PDF export failed")); }
  };

  if (!data) return <div className="text-sm text-foreground/60">Loading…</div>;
  const c = data.counts || {};

  return (
    <div className="space-y-4" data-testid="asset-inspections-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 w-40 rounded-none text-xs" data-testid="insp-month" />
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={load} data-testid="insp-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
        <Button size="sm" className="rounded-none h-8 ml-auto" onClick={downloadPdf} data-testid="insp-pdf"><FileDown className="w-3.5 h-3.5 mr-1" />Print PDF</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="insp-stats">
        <Stat label="Compliance" value={`${c.compliance_pct ?? 0}%`} tone={c.compliance_pct >= 80 ? "ok" : c.compliance_pct >= 50 ? "warn" : "danger"} testid="insp-stat-compliance" />
        <Stat label="Assets w/ cadence" value={c.with_cadence} testid="insp-stat-cadence" />
        <Stat label="Overdue" value={c.overdue} tone={c.overdue ? "danger" : "ok"} testid="insp-stat-overdue" />
        <Stat label="Due this month" value={c.this_month} testid="insp-stat-due" />
        <Stat label="Never inspected" value={c.never_inspected} tone={c.never_inspected ? "warn" : "ok"} testid="insp-stat-never" />
      </div>

      <Bucket title="Overdue" icon={AlertCircle} tone="danger" rows={data.overdue} onInspect={setInspecting} testKey="overdue" />
      <Bucket title="Due this month" icon={CalendarCheck} rows={data.this_month} onInspect={setInspecting} testKey="due" />
      <Bucket title="Never inspected" icon={ClipboardCheck} tone="warn" rows={data.never_inspected} onInspect={setInspecting} testKey="never" />

      {inspecting && <InspectionDialog asset={inspecting} onClose={() => setInspecting(null)} onSaved={async () => { setInspecting(null); await load(); }} />}
    </div>
  );
}

function Bucket({ title, icon: Icon, rows, onInspect, tone = "ok", testKey }) {
  const toneCls = tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-foreground";
  return (
    <div className="border border-border" data-testid={`insp-bucket-${testKey}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b border-border">
        <Icon className={`w-4 h-4 ${toneCls}`} />
        <span className="text-sm font-bold">{title}</span>
        <span className="text-[11px] text-muted-foreground">({rows.length})</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-xs text-muted-foreground">Nothing here.</div>
      ) : (
        <div className="divide-y divide-border">
          <div className="grid grid-cols-12 gap-2 px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
            <div className="col-span-2">Tag</div>
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Location</div>
            <div className="col-span-1">Cadence</div>
            <div className="col-span-1">Next due</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {rows.map((a) => (
            <div key={a.id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm" data-testid={`insp-row-${a.id}`}>
              <div className="col-span-2 font-mono text-xs truncate">{a.asset_tag || "—"}</div>
              <div className="col-span-3 truncate font-medium">{a.name}</div>
              <div className="col-span-2 text-xs truncate">{a.category}</div>
              <div className="col-span-2 text-xs truncate text-muted-foreground">{a.location || "—"}</div>
              <div className="col-span-1 font-mono text-xs">{a.inspection_cadence_days || "—"}d</div>
              <div className="col-span-1 font-mono text-xs">{(a.next_inspection_at || "—").slice(0, 10)}</div>
              <div className="col-span-1 text-right">
                <Button size="sm" className="rounded-none h-7" onClick={() => onInspect(a)} data-testid={`insp-log-${a.id}`}>
                  Inspect
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InspectionDialog({ asset, onClose, onSaved }) {
  const [form, setForm] = useState({ condition: asset.condition || "good", notes: "", passed: true, photo_urls: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const photo_urls = form.photo_urls.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post(`/assets/${asset.id}/inspect`, {
        condition: form.condition, notes: form.notes || null,
        passed: form.passed, photo_urls,
      });
      toast.success("Inspection logged");
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="insp-dialog">
        <DialogHeader><DialogTitle>Inspect · {asset.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            <span className="font-mono">{asset.asset_tag || "no tag"}</span> · cadence {asset.inspection_cadence_days || "—"}d · last {asset.last_inspected_at?.slice(0, 10) || "never"}
          </div>
          <div>
            <Label className="label-eyebrow">Condition</Label>
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger className="h-9 rounded-none" data-testid="insp-condition"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONDS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="min-h-[70px] rounded-none" data-testid="insp-notes" />
          </div>
          <div>
            <Label className="label-eyebrow">Photo URLs (comma-separated)</Label>
            <Input value={form.photo_urls} onChange={(e) => setForm({ ...form, photo_urls: e.target.value })} placeholder="https://…, https://…" className="h-9 rounded-none" data-testid="insp-photos" />
          </div>
          <label className="text-xs inline-flex items-center gap-1.5">
            <input type="checkbox" checked={form.passed} onChange={(e) => setForm({ ...form, passed: e.target.checked })} data-testid="insp-passed" />
            Passed inspection
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="insp-save">{busy ? "Saving…" : "Log inspection"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid, tone = "ok" }) {
  const c = tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "";
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`text-2xl font-black font-mono ${c}`}>{value ?? 0}</div>
    </div>
  );
}
